'use strict';

const $ = require('jquery');
const {
    queryFirst,
    queryAll,
    addClass,
    removeClass,
    hasClass,
    debounce
} = require('../domUtil');

const SCROLLABLE_CONTENT_SELECTOR = queryAll('.scrollable-content');
const CAROUSEL_INDICATORS_ENABLED_SELECTOR = queryAll('.carousel.carousel-indicators-enabled');
const LEFT_ARROW_SELECTOR = '.scroll-left-arrow';
const RIGHT_ARROW_SELECTOR = '.scroll-right-arrow';
const MOBILE_CAROUSEL_OVERLAY_MQ = '(max-width: 767.98px)';
/** Temporary class while measuring slide heights (styles in `fullWidthCarouselComponent.scss`). */
const CAROUSEL_ITEM_MEASURE_HEIGHT_CLASS = 'carousel-item--measure-height';
const CAROUSEL_ITEM_MEASURE_WIDTH_VAR = '--carousel-item-measure-width';

/**
 * Initialize the carousel.
 * @param {HTMLElement} scrollableContainerEl - The scrollable container element.
 */
function initSpecificCarousel(scrollableContainerEl) {
    const parentEl = scrollableContainerEl.parentElement;

    // Attaching events on Left arrow click
    const leftArrowEl = queryFirst(LEFT_ARROW_SELECTOR, parentEl);
    if (leftArrowEl) {
        leftArrowEl.addEventListener('click', () => {
            scrollableContainerEl.scrollTo({
                left: Math.max(0, scrollableContainerEl.scrollLeft - scrollableContainerEl.offsetWidth),
                behavior: 'smooth'
            });
        });
    }

    // Attaching events on Right arrow click
    const rightArrowEl = queryFirst(RIGHT_ARROW_SELECTOR, parentEl);
    if (rightArrowEl) {
        rightArrowEl.addEventListener('click', () => {
            scrollableContainerEl.scrollTo({
                left: Math.min(scrollableContainerEl.scrollWidth, scrollableContainerEl.scrollLeft + scrollableContainerEl.offsetWidth),
                behavior: 'smooth'
            });
        });
    }

    // Hide left arrow on carousel initialization.
    addClass(leftArrowEl, 'hide');

    // Hide both the arrows, if the items are not scrollable.
    if (scrollableContainerEl.scrollWidth <= scrollableContainerEl.offsetWidth) {
        addClass(rightArrowEl, 'hide');
    } else {
        removeClass(rightArrowEl, 'hide');
        const callback = () => {
            const { scrollWidth, scrollLeft, offsetWidth } = scrollableContainerEl;
            const offsetValue = 5;
            if (scrollWidth - scrollLeft - offsetWidth < offsetValue) {
                // Hide the right arrow, if content is scrolled to right end.
                if (hasClass(leftArrowEl, 'hide')) {
                    removeClass(leftArrowEl, 'hide');
                }
                if (!hasClass(rightArrowEl, 'hide')) {
                    addClass(rightArrowEl, 'hide');
                }
            } else if (scrollLeft === 0) {
                // Hide the left arrow, if content is scrolled to left end.
                if (!hasClass(leftArrowEl, 'hide')) {
                    addClass(leftArrowEl, 'hide');
                }
                if (hasClass(rightArrowEl, 'hide')) {
                    removeClass(rightArrowEl, 'hide');
                }
            } else {
                // Display both the arrows, if content is scrolled neither of the ends.
                if (hasClass(leftArrowEl, 'hide')) {
                    removeClass(leftArrowEl, 'hide');
                }
                if (hasClass(rightArrowEl, 'hide')) {
                    removeClass(rightArrowEl, 'hide');
                }
            }
        };
        scrollableContainerEl.addEventListener('scroll', debounce(callback, 300));
    }
}

/**
 * Dot "filled" state: cumulative while advancing; on slide 0 only the first dot is filled (including after wrap).
 *
 * @param {HTMLElement} carouselRoot - .carousel.carousel-indicators-enabled
 * @param {number} activeIndex - index of active .carousel-item
 */
function syncIndicatorFilledClass(carouselRoot, activeIndex) {
    const indicators = queryAll('.carousel-indicators li.indicator', carouselRoot);
    indicators.forEach((li, i) => {
        const filled = activeIndex === 0 ? i === 0 : i <= activeIndex;
        if (filled) {
            addClass(li, 'filled');
        } else {
            removeClass(li, 'filled');
        }
    });
}

/**
 * Match aria-current on dot buttons to active indicator li.
 * @param {HTMLElement} carouselRoot - .carousel.carousel-indicators-enabled
 */
function syncIndicatorAriaCurrent(carouselRoot) {
    queryAll('.carousel-indicators li.indicator', carouselRoot).forEach((li) => {
        const btn = queryFirst('button.carousel-indicator-btn', li);
        if (!btn) {
            return;
        }
        if (hasClass(li, 'active')) {
            btn.setAttribute('aria-current', 'true');
        } else {
            btn.removeAttribute('aria-current');
        }
    });
}

/**
 * Clear image-based overlay positioning (controls / dots vs hero image).
 * @param {HTMLElement} carouselRoot - .carousel.carousel-indicators-enabled
 */
function clearCarouselImageMetrics(carouselRoot) {
    carouselRoot.style.removeProperty('--carousel-image-top');
    carouselRoot.style.removeProperty('--carousel-image-height');
    removeClass(carouselRoot, 'carousel-image-metrics');
}

/**
 * Measure slides height off screen, because inactive slides are hidden.
 * @param {HTMLElement} carouselRoot - .carousel.carousel-indicators-enabled
 * @returns {number} max height in px
 */
function measureCarouselItemsmaxHeighteight(carouselRoot) {
    const inner = queryFirst('.carousel-inner', carouselRoot);
    if (!inner) {
        return 0;
    }
    const items = queryAll('.carousel-item', inner);
    if (!items.length) {
        return 0;
    }
    const innerWidth = inner.offsetWidth;
    let maxHeight = 0;

    inner.style.setProperty(CAROUSEL_ITEM_MEASURE_WIDTH_VAR, `${innerWidth}px`);
    try {
        items.forEach((item) => {
            addClass(item, CAROUSEL_ITEM_MEASURE_HEIGHT_CLASS);
            try {
                const h = item.offsetHeight;
                if (h > maxHeight) {
                    maxHeight = h;
                }
            } finally {
                removeClass(item, CAROUSEL_ITEM_MEASURE_HEIGHT_CLASS);
            }
        });
    } finally {
        inner.style.removeProperty(CAROUSEL_ITEM_MEASURE_WIDTH_VAR);
    }

    return maxHeight;
}

/**
 * Mobile: set all `.carousel-item` min-heights to the tallest slide (CSS var on root).
 * @param {HTMLElement} carouselRoot - .carousel.carousel-indicators-enabled
 */
function syncCarouselEqualSlideHeights(carouselRoot) {
    if (!window.matchMedia(MOBILE_CAROUSEL_OVERLAY_MQ).matches) {
        carouselRoot.style.removeProperty('--carousel-equal-slide-height');
        removeClass(carouselRoot, 'carousel-slides-equal-height');
        return;
    }
    const maxHeight = measureCarouselItemsmaxHeighteight(carouselRoot);
    if (maxHeight < 1) {
        carouselRoot.style.removeProperty('--carousel-equal-slide-height');
        removeClass(carouselRoot, 'carousel-slides-equal-height');
        return;
    }
    carouselRoot.style.setProperty('--carousel-equal-slide-height', `${Math.round(maxHeight)}px`);
    addClass(carouselRoot, 'carousel-slides-equal-height');
}

/**
 * Position carousel arrows controls (vertical center) and dots indicators (bottom) relative to the active
 * slide's hero img inside .image-component, not the full slide height.
 *
 * @param {HTMLElement} carouselRoot - .carousel.carousel-indicators-enabled
 */
function syncCarouselOverlayMetrics(carouselRoot) {
    if (!window.matchMedia(MOBILE_CAROUSEL_OVERLAY_MQ).matches) {
        clearCarouselImageMetrics(carouselRoot);
        return;
    }
    const activeItem = queryFirst('.carousel-item.active', carouselRoot)
        || queryFirst('.carousel-item', carouselRoot);
    if (!activeItem) {
        clearCarouselImageMetrics(carouselRoot);
        return;
    }
    const imageComponent = queryFirst('.image-component', activeItem) || queryFirst('.left-image-container', activeItem);
    if (!imageComponent) {
        clearCarouselImageMetrics(carouselRoot);
        return;
    }
    const imgEl = queryFirst('img', imageComponent) || queryFirst('video.d-md-none', imageComponent);
    if (!imgEl) {
        clearCarouselImageMetrics(carouselRoot);
        return;
    }
    const carouselRect = carouselRoot.getBoundingClientRect();
    const imageRect = imgEl.getBoundingClientRect();
    if (imageRect.height < 1) {
        clearCarouselImageMetrics(carouselRoot);
        return;
    }
    const top = imageRect.top - carouselRect.top;
    const height = imageRect.height;
    carouselRoot.style.setProperty('--carousel-image-top', `${Math.round(top)}px`);
    carouselRoot.style.setProperty('--carousel-image-height', `${Math.round(height)}px`);
    addClass(carouselRoot, 'carousel-image-metrics');
}

/**
 * Sync overlay metrics after slide change, image load, and on animation frames.
 * @param {HTMLElement} carouselRoot - .carousel.carousel-indicators-enabled
 */
function bindCarouselImageMetrics(carouselRoot) {
    const runMobileLayout = () => {
        syncCarouselEqualSlideHeights(carouselRoot);
        syncCarouselOverlayMetrics(carouselRoot);
    };
    const scheduleSync = () => {
        window.requestAnimationFrame(() => runMobileLayout());
    };

    runMobileLayout();
    scheduleSync();

    $(carouselRoot).on('slid.bs.carousel', scheduleSync);

    queryAll('.carousel-item .image-component img', carouselRoot).forEach((img) => {
        if (img.complete) {
            return;
        }
        img.addEventListener('load', scheduleSync, { once: true });
    });
}

/**
 * Dynamically updates carousel arrow and dot colors based on the current active slide.
 */
function initDynamicCarouselColors() {
    CAROUSEL_INDICATORS_ENABLED_SELECTOR.forEach((carouselEl) => {
        const indicatorsContainer = queryFirst('.carousel-indicators', carouselEl);
        if (!indicatorsContainer) {
            return;
        }

        const updateCarouselColors = (index) => {
            const indicators = queryAll('.indicator', indicatorsContainer);
            const targetIndicator = (typeof index !== 'undefined' && indicators[index]) 
                ? indicators[index] 
                : queryFirst('.indicator.active', indicatorsContainer);

            if (!targetIndicator) {
                return;
            }

            const dotColorDesktop = targetIndicator.getAttribute('data-desktop-dot-color');
            const dotColorMobile = targetIndicator.getAttribute('data-mobile-dot-color');
            const arrowColorDesktop = targetIndicator.getAttribute('data-desktop-arrow-color');
            const arrowColorMobile = targetIndicator.getAttribute('data-mobile-arrow-color');

            if (dotColorDesktop) {
                carouselEl.style.setProperty('--desktop-dots-color-dynamic', dotColorDesktop);
            } else {
                carouselEl.style.removeProperty('--desktop-dots-color-dynamic');
            }

            if (dotColorMobile) {
                carouselEl.style.setProperty('--mobile-dots-color-dynamic', dotColorMobile);
            } else {
                carouselEl.style.removeProperty('--mobile-dots-color-dynamic');
            }

            if (arrowColorDesktop) {
                carouselEl.style.setProperty('--desktop-arrow-color-dynamic', arrowColorDesktop);
            } else {
                carouselEl.style.removeProperty('--desktop-arrow-color-dynamic');
            }

            if (arrowColorMobile) {
                carouselEl.style.setProperty('--mobile-arrow-color-dynamic', arrowColorMobile);
            } else {
                carouselEl.style.removeProperty('--mobile-arrow-color-dynamic');
            }
        };

        updateCarouselColors();

        $(carouselEl).on('slide.bs.carousel', function (e) {
            if (typeof e.to !== 'undefined') {
                updateCarouselColors(e.to);
            }
        });
    });
}

/**
 * Bind slid.bs.carousel handlers so indicator dots get a cumulative .filled class (reset on slide 0).
 */
function initIndicatorsFilledOnCarousels() {
    CAROUSEL_INDICATORS_ENABLED_SELECTOR.forEach((carouselEl) => {
        if (!queryFirst('.carousel-indicators', carouselEl)) {
            return;
        }
        syncIndicatorAriaCurrent(carouselEl);
        $(carouselEl).on('slid.bs.carousel', function (e) {
            let activeItem = e.relatedTarget;
            if (!activeItem || !carouselEl.contains(activeItem)) {
                activeItem = queryFirst('.carousel-inner .carousel-item.active', carouselEl);
            }
            const items = queryAll('.carousel-inner .carousel-item', carouselEl);
            const activeIndex = items.indexOf(activeItem);
            if (activeIndex < 0) {
                return;
            }
            syncIndicatorFilledClass(carouselEl, activeIndex);
            syncIndicatorAriaCurrent(carouselEl);
        });
    });
}

const syncAllCarouselMobileLayout = debounce(() => {
    CAROUSEL_INDICATORS_ENABLED_SELECTOR.forEach((el) => {
        syncCarouselEqualSlideHeights(el);
        syncCarouselOverlayMetrics(el);
    });
}, 150);

let carouselOverlayResizeBound = false;
let carouselOverlayMediaBound = false;

/**
 * Controls / dots aligned to .image-component height for full-width PD carousels (mobile only).
 */
function initCarouselOverlayMetrics() {
    CAROUSEL_INDICATORS_ENABLED_SELECTOR.forEach((carouselEl) => {
        bindCarouselImageMetrics(carouselEl);
    });
    if (!carouselOverlayResizeBound) {
        window.addEventListener('resize', syncAllCarouselMobileLayout);
        carouselOverlayResizeBound = true;
    }
    if (!carouselOverlayMediaBound) {
        const mq = window.matchMedia(MOBILE_CAROUSEL_OVERLAY_MQ);
        const onViewportChange = () => {
            CAROUSEL_INDICATORS_ENABLED_SELECTOR.forEach((el) => {
                syncCarouselEqualSlideHeights(el);
                syncCarouselOverlayMetrics(el);
            });
        };
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', onViewportChange);
        } else {
            mq.addListener(onViewportChange);
        }
        carouselOverlayMediaBound = true;
    }
}

/**
 * Handles horizontal touch swipes for a carousel element while ignoring 
 * diagonal gestures (~45°) to preserve natural vertical page scrolling.
 */
function initCarouselSwipeHandler() {
    queryAll('.js-carousel-swipe-handler').forEach((carouselEl) => {
        let touchStartX = 0;
        let touchStartY = 0;
        let isHorizontalSwipe = false;

        const minHorizontalSwipe = 40;

        carouselEl.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
            isHorizontalSwipe = false;
        }, { passive: true });

        carouselEl.addEventListener('touchmove', (e) => {
            if (!touchStartX || !touchStartY) return;

            const currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;

            const deltaX = currentX - touchStartX;
            const deltaY = currentY - touchStartY;

            const absX = Math.abs(deltaX);
            const absY = Math.abs(deltaY);

            if (absX > absY && absX > 10) {
                isHorizontalSwipe = true;

                if (e.cancelable) {
                    e.preventDefault();
                }
            }
        }, { passive: false });

        carouselEl.addEventListener('touchend', (e) => {
            if (!isHorizontalSwipe) return;

            const touchEndX = e.changedTouches[0].clientX;
            const deltaX = touchEndX - touchStartX;

            if (Math.abs(deltaX) > minHorizontalSwipe) {
                if (deltaX > 0) {
                    $(carouselEl).carousel('prev');
                } else {
                    $(carouselEl).carousel('next');
                }
            }

            touchStartX = 0;
            touchStartY = 0;
            isHorizontalSwipe = false;
        }, { passive: true });
    });
}

module.exports = {
    initCarousel: function () {
        SCROLLABLE_CONTENT_SELECTOR.forEach(scrollableContainerEl => {
            initSpecificCarousel(scrollableContainerEl);
        });
        initCarouselOverlayMetrics();
        initIndicatorsFilledOnCarousels();
        initDynamicCarouselColors();
        initCarouselSwipeHandler();
    },
    initSpecificCarousel: initSpecificCarousel
};
